import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SUPABASE_URL } from '@/integrations/supabase/client';

/**
 * Customer-facing 5-star page at /r/:token, opened from the review request
 * SMS or email. No login. The tenant picks where each star goes; a star with
 * no link shows a thank-you screen with an optional comment box.
 */

interface PageData {
  preview: boolean;
  brand_name: string;
  logo_url: string | null;
  primary_color: string | null;
  prompt: string;
  links: Record<string, string>;
  rating: number | null;
  comment: string | null;
}

type Phase = 'loading' | 'invalid' | 'pick' | 'sending' | 'thanks' | 'redirecting';

export default function ReviewStars() {
  const { token = '' } = useParams<{ token: string }>();
  const [data, setData] = useState<PageData | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [hover, setHover] = useState(0);
  const [picked, setPicked] = useState(0);
  const [comment, setComment] = useState('');
  const [commentSent, setCommentSent] = useState(false);
  const [commentBusy, setCommentBusy] = useState(false);

  useEffect(() => {
    document.title = 'How did we do?';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/review-page?token=${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error('invalid');
        const d = (await res.json()) as PageData;
        if (cancelled) return;
        setData(d);
        if (d.rating) {
          setPicked(d.rating);
          setComment(d.comment ?? '');
          setPhase('thanks');
        } else {
          setPhase('pick');
        }
      } catch {
        if (!cancelled) setPhase('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (rating: number, withComment?: string) => {
    if (!data) return;
    setPicked(rating);
    const isComment = withComment !== undefined;
    if (isComment) setCommentBusy(true); else setPhase('sending');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/review-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rating, ...(withComment !== undefined ? { comment: withComment } : {}) }),
      });
      const out = (await res.json()) as { ok?: boolean; redirect_url?: string | null };
      if (!res.ok) throw new Error('failed');
      if (out.redirect_url && withComment === undefined) {
        setPhase('redirecting');
        window.location.assign(out.redirect_url);
        return;
      }
      if (isComment) setCommentSent(true);
      setPhase('thanks');
    } catch {
      if (!isComment) setPhase('pick');
    } finally {
      if (isComment) setCommentBusy(false);
    }
  };

  const accent = data?.primary_color || '#1e3a8a';
  const starFill = '#f5b301';
  const returnLink = data && picked ? data.links[String(picked)] ?? null : null;

  if (phase === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </main>
    );
  }

  if (phase === 'invalid' || !data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-sm text-center">
          <h1 className="text-xl font-semibold">This link is not valid</h1>
          <p className="mt-2 text-sm text-muted-foreground">It may have been typed incorrectly. Please use the link from your message.</p>
        </div>
      </main>
    );
  }

  const active = hover || picked;

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      {data.preview && (
        <div className="mb-6 rounded-md border border-warning/40 bg-warning/5 px-3 py-1.5 text-xs text-muted-foreground">
          Preview. Nothing you tap here is recorded.
        </div>
      )}
      <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
        {data.logo_url ? (
          <img src={data.logo_url} alt={data.brand_name} className="mx-auto mb-5 max-h-16 max-w-[200px] object-contain" />
        ) : (
          <div className="mb-5 text-lg font-semibold" style={{ color: accent }}>{data.brand_name}</div>
        )}

        {phase !== 'thanks' && phase !== 'redirecting' ? (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">{data.prompt}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Tap a star to rate {data.brand_name}.</p>

            <div className="mt-7 flex justify-center gap-2" role="radiogroup" aria-label="Rating out of five stars"
              onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={picked === n}
                  aria-label={`${n} star${n === 1 ? '' : 's'}`}
                  disabled={phase === 'sending'}
                  onMouseEnter={() => setHover(n)}
                  onFocus={() => setHover(n)}
                  onClick={() => submit(n)}
                  className="rounded-full p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  <Star
                    className="h-11 w-11"
                    strokeWidth={1.5}
                    style={{ color: n <= active ? starFill : 'hsl(var(--muted-foreground) / 0.4)', fill: n <= active ? starFill : 'transparent' }}
                  />
                </button>
              ))}
            </div>
            {phase === 'sending' && (
              <p className="mt-5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> One moment…
              </p>
            )}
          </>
        ) : phase === 'redirecting' ? (
          <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Taking you there…
          </p>
        ) : (
          <>
            <div className="flex justify-center gap-1" aria-label={`You gave ${picked} star${picked === 1 ? '' : 's'}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="h-8 w-8" strokeWidth={1.5}
                  style={{ color: n <= picked ? starFill : 'hsl(var(--muted-foreground) / 0.4)', fill: n <= picked ? starFill : 'transparent' }} />
              ))}
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight">Thank you</h1>
            <p className="mt-1 text-sm text-muted-foreground">Your rating has been passed on to {data.brand_name}.</p>

            {/* Returning visitor (Back from the review site, or the link opened
                again) whose star has a link: give them the way back so a review
                they left half-written is not lost. First visits with a link
                redirect and never reach this screen. */}
            {returnLink && (
              <div className="mt-6 space-y-2">
                <p className="text-sm text-muted-foreground">Didn't get to finish your review?</p>
                <Button asChild className="w-full" style={{ backgroundColor: accent }}>
                  <a href={returnLink} rel="noreferrer">Leave your review</a>
                </Button>
              </div>
            )}

            {!commentSent ? (
              <div className="mt-6 space-y-3 text-left">
                <label htmlFor="rv-comment" className="text-sm font-medium">
                  {returnLink ? `Or send a note to ${data.brand_name}` : 'Anything you would like to add?'}{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Textarea id="rv-comment" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000}
                  placeholder="What went well, or what we could do better" />
                <Button className="w-full" variant={returnLink ? 'outline' : 'default'}
                  style={returnLink ? undefined : { backgroundColor: accent }} disabled={!comment.trim() || commentBusy}
                  onClick={() => submit(picked, comment.trim())}>
                  {commentBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Send
                </Button>
              </div>
            ) : (
              <p className="mt-6 text-sm text-muted-foreground">Your comment has been sent. You can close this page.</p>
            )}
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Powered by Steddy</p>
    </main>
  );
}
